// src/components/RotinaFechamento.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './RotinaFechamento.css';
import {
    FaArrowLeft, FaFolder, FaCheckCircle, FaExclamationTriangle,
    FaSearch, FaFileInvoiceDollar, FaTimes, FaArchive, FaInbox,
    FaRobot, FaLightbulb, FaChartLine, FaCalendarAlt,
    FaTrash, FaFolderPlus, FaLevelUpAlt, FaChevronLeft, FaChevronRight,
    FaArrowUp, FaArrowDown, FaMinus, FaBolt, FaEnvelope, FaCheckDouble,
    FaRegCalendarAlt, FaFire, FaTachometerAlt, FaCoins,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

// ─── FORMATADORES ─────────────────────────────────────────────────────────────
const formatDate = (ds) => {
    if (!ds) return '-';
    const d = new Date(ds.includes('T') ? ds : ds + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};
const formatDateShort = (ds) => {
    if (!ds) return '-';
    const d = new Date(ds.includes('T') ? ds : ds + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
};
const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
const formatCurrencyCompact = (val) => {
    const n = Math.abs(val || 0);
    if (n >= 1_000_000) return `R$ ${(val / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `R$ ${(val / 1_000).toFixed(1)}k`;
    return formatCurrency(val);
};

// ─── HEALTH SCORE ─────────────────────────────────────────────────────────────
const calcHealthScore = (arquivo) => {
    let score = 0;
    if (arquivo.status === 'convergente')  score += 40;
    else if (arquivo.status === 'sem_movimento') score += 20;
    if (parseFloat(arquivo.saldo_dia || 0) > 0) score += 35;
    else if (parseFloat(arquivo.saldo_dia || 0) === 0) score += 15;
    if ((arquivo.qtd_transacoes || 0) >= 5) score += 15;
    else if ((arquivo.qtd_transacoes || 0) > 0) score += 8;
    if (arquivo.tem_analise_ia) score += 10;
    return Math.min(100, Math.max(0, score));
};
const scoreLabel = (s) => {
    if (s >= 80) return { label: 'Excelente', cls: 'score-excellent' };
    if (s >= 60) return { label: 'Bom',       cls: 'score-good' };
    if (s >= 40) return { label: 'Regular',   cls: 'score-regular' };
    return              { label: 'Crítico',   cls: 'score-critical' };
};

// ─── SPARKLINE SVG ────────────────────────────────────────────────────────────
const Sparkline = ({ data = [], width = 80, height = 28, color = '#10b981' }) => {
    if (data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pts = data.map((v, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return (
        <svg width={width} height={height} className="sparkline">
            <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

// ─── HEATMAP CALENDAR ─────────────────────────────────────────────────────────
const HeatmapCalendar = ({ arquivos, onCellClick }) => {
    const weeks = 13;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Monta lookup por data YYYY-MM-DD
    const lookup = useMemo(() => {
        const m = {};
        arquivos.forEach(a => { m[a.data_referencia] = a; });
        return m;
    }, [arquivos]);

    // Gera grade de 13 semanas × 7 dias
    const grid = useMemo(() => {
        const cells = [];
        // Começa no domingo da semana de 13 semanas atrás
        const start = new Date(today);
        start.setDate(start.getDate() - (weeks * 7 - 1));
        start.setDate(start.getDate() - start.getDay()); // volta para domingo

        for (let w = 0; w < weeks; w++) {
            const week = [];
            for (let d = 0; d < 7; d++) {
                const dt = new Date(start);
                dt.setDate(start.getDate() + w * 7 + d);
                const key = dt.toISOString().split('T')[0];
                const isFuture = dt > today;
                week.push({ date: dt, key, data: lookup[key] || null, isFuture });
            }
            cells.push(week);
        }
        return cells;
    }, [lookup, today]);

    const monthLabels = useMemo(() => {
        const labels = [];
        grid.forEach((week, wi) => {
            const firstOfWeek = week[0].date;
            if (wi === 0 || firstOfWeek.getDate() <= 7) {
                labels.push({ week: wi, label: firstOfWeek.toLocaleDateString('pt-BR', { month: 'short' }) });
            }
        });
        // Dedup
        const seen = new Set();
        return labels.filter(l => { if (seen.has(l.label)) return false; seen.add(l.label); return true; });
    }, [grid]);

    const dayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    return (
        <div className="heatmap-wrapper">
            <div className="heatmap-month-labels">
                {monthLabels.map((m, i) => (
                    <span key={i} style={{ gridColumn: m.week + 1 }}>{m.label}</span>
                ))}
            </div>
            <div className="heatmap-grid-area">
                <div className="heatmap-day-labels">
                    {dayLabels.map((d, i) => <span key={i}>{i % 2 === 1 ? d : ''}</span>)}
                </div>
                <div className="heatmap-grid" style={{ gridTemplateColumns: `repeat(${weeks}, 1fr)` }}>
                    {grid.map((week, wi) =>
                        week.map((cell, di) => {
                            let cls = 'hm-cell';
                            if (cell.isFuture) cls += ' hm-future';
                            else if (!cell.data) cls += ' hm-empty';
                            else if (cell.data.status === 'convergente') cls += ' hm-ok';
                            else if (cell.data.status === 'divergente') cls += ' hm-bad';
                            else cls += ' hm-neutral';

                            const isToday = cell.key === today.toISOString().split('T')[0];
                            if (isToday) cls += ' hm-today';

                            return (
                                <div
                                    key={`${wi}-${di}`}
                                    className={cls}
                                    title={cell.data
                                        ? `${formatDateShort(cell.key)}\nSaldo: ${formatCurrency(cell.data.saldo_dia)}\nStatus: ${cell.data.status}`
                                        : formatDateShort(cell.key)
                                    }
                                    onClick={() => cell.data && onCellClick(cell.data)}
                                />
                            );
                        })
                    )}
                </div>
            </div>
            <div className="heatmap-legend">
                <span className="hm-cell hm-ok" /><span>Convergente</span>
                <span className="hm-cell hm-bad" /><span>Divergente</span>
                <span className="hm-cell hm-neutral" /><span>Sem movimento</span>
                <span className="hm-cell hm-empty" /><span>Sem dados</span>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
export default function RotinaFechamento() {
    const navigate = useNavigate();

    // ── DADOS ─────────────────────────────────────────────────────────────────
    const [pastasCustom,      setPastasCustom]      = useState([]);
    const [arquivos,          setArquivos]          = useState([]);
    const [allArquivos,       setAllArquivos]       = useState([]); // para o heatmap
    const [insights,          setInsights]          = useState(null);
    const [currentViewFolder, setCurrentViewFolder] = useState(null);

    // ── UI ────────────────────────────────────────────────────────────────────
    const [loading,            setLoading]            = useState(true);
    const [selectedDossieId,   setSelectedDossieId]   = useState(null);
    const [dossieDetails,      setDossieDetails]      = useState(null);
    const [searchTerm,         setSearchTerm]         = useState('');
    const [analyzing,          setAnalyzing]          = useState(false);
    const [sendingEmail,       setSendingEmail]       = useState(false);
    const [currentUser,        setCurrentUser]        = useState(null);
    const [activeInsightTab,   setActiveInsightTab]   = useState('overview');

    const overlayRef = useRef(null);

    const isAdminGeral  = currentUser?.perfil === 'admin_geral';
    const temAcessoFull = ['admin_geral', 'admin', 'financeiro'].includes(currentUser?.perfil);

    // ── FETCH ─────────────────────────────────────────────────────────────────
    const fetchStructure = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = currentViewFolder ? { pasta_id: currentViewFolder.id } : {};
            const { data } = await api.get('/api/financeiro/organizacao', {
                headers: { Authorization: `Bearer ${token}` }, params
            });
            setPastasCustom(data.pastas || []);
            setArquivos(data.arquivos || []);
        } catch { toast.error('Erro ao carregar estrutura.'); }
        finally { setLoading(false); }
    }, [currentViewFolder]);

    const fetchAllArquivos = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await api.get('/api/financeiro/rotina-fechamento', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllArquivos(data || []);
        } catch {}
    }, []);

    const fetchInsights = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const [kpisPagar, kpisReceber, carteira] = await Promise.all([
                api.get('/api/financeiro/kpis-pagar',    { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
                api.get('/api/financeiro/kpis-receber',  { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
                api.get('/api/financeiro/carteira',       { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
            ]);
            setInsights({
                pagar:   kpisPagar?.data   || {},
                receber: kpisReceber?.data || {},
                carteira: carteira?.data   || {},
            });
        } catch {}
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch {} }
        fetchStructure();
        fetchAllArquivos();
        fetchInsights();
    }, [currentViewFolder, fetchStructure, fetchAllArquivos, fetchInsights]);

    // ── PASTAS ────────────────────────────────────────────────────────────────
    const handleCreateFolder = async () => {
        const nome = prompt('Nome da Nova Pasta (Ex: Novembro 2025):');
        if (!nome) return;
        try {
            const token = localStorage.getItem('token');
            await api.post('/api/financeiro/pastas', { nome }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Pasta criada!');
            fetchStructure();
        } catch { toast.error('Erro ao criar pasta.'); }
    };

    const handleDeleteCustomFolder = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Excluir pasta? Os arquivos voltarão para a Raiz.')) return;
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/api/financeiro/pastas/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Pasta excluída.');
            fetchStructure();
        } catch { toast.error('Erro ao excluir pasta.'); }
    };

    // ── DRAG AND DROP ─────────────────────────────────────────────────────────
    const handleDragStart = (e, fileId) => {
        e.dataTransfer.setData('fileId', fileId);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const handleDropOnFolder = async (e, targetFolderId) => {
        e.preventDefault();
        const fileId = e.dataTransfer.getData('fileId');
        if (!fileId) return;
        setArquivos(prev => prev.filter(f => f.id !== parseInt(fileId)));
        try {
            const token = localStorage.getItem('token');
            await api.put(`/api/financeiro/rotina-fechamento/${fileId}/mover`,
                { pasta_id: targetFolderId }, { headers: { Authorization: `Bearer ${token}` } });
            toast.info('Dossiê movido.');
        } catch { toast.error('Falha ao mover.'); fetchStructure(); }
    };

    // ── DOSSIÊ ────────────────────────────────────────────────────────────────
    const handleOpenDossie = async (id) => {
        setSelectedDossieId(id);
        setDossieDetails(null);
        try {
            const token = localStorage.getItem('token');
            const { data } = await api.get(`/api/financeiro/rotina-fechamento/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDossieDetails(data);
        } catch { toast.error('Erro ao abrir dossiê.'); setSelectedDossieId(null); }
    };

    const handleOverlayClick = (e) => {
        if (overlayRef.current && e.target === overlayRef.current) setSelectedDossieId(null);
    };

    const handleDeleteDossie = async (e, id) => {
        e.stopPropagation();
        if (!isAdminGeral) return;
        if (!window.confirm('⚠️ Confirmar exclusão definitiva?')) return;
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/api/financeiro/rotina-fechamento/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Dossiê removido.');
            fetchStructure(); fetchAllArquivos();
        } catch { toast.error('Erro ao excluir.'); }
    };

    // ── PROCESSAR MANUAL ──────────────────────────────────────────────────────
    const handleProcessarManual = async () => {
        if (!isAdminGeral) { toast.warning('🚫 Restrito ao Admin Geral.'); return; }
        const ontem = new Date();
        ontem.setDate(ontem.getDate() - 1);
        const padraoOntem = ontem.toISOString().split('T')[0];
        const targetDate = window.prompt('📅 Data para reprocessar (AAAA-MM-DD):', padraoOntem);
        if (!targetDate) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await api.post('/api/financeiro/rotina-fechamento/processar-manual',
                { data: targetDate }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`✅ Fechamento de ${targetDate} processado!`);
            fetchStructure(); fetchAllArquivos();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Erro ao processar.');
        } finally { setLoading(false); }
    };

    // ── ANÁLISE IA ────────────────────────────────────────────────────────────
    const handleAnalyzeAI = async () => {
        if (!selectedDossieId) return;
        setAnalyzing(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await api.post(
                `/api/financeiro/rotina-fechamento/${selectedDossieId}/analisar-ia`, {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDossieDetails(prev => ({
                ...prev,
                detalhes_json: { ...prev.detalhes_json, ai_analysis: data.analysis }
            }));
            toast.success('Auditoria IA salva!');
        } catch { toast.error('Auditor IA indisponível.'); }
        finally { setAnalyzing(false); }
    };

    // ── ENVIAR E-MAIL MANUAL ──────────────────────────────────────────────────
    const handleSendEmail = async () => {
        if (!selectedDossieId || !dossieDetails) return;
        setSendingEmail(true);
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/api/financeiro/rotina-fechamento/${selectedDossieId}/enviar-email`, {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('📧 Relatório enviado por e-mail!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao enviar e-mail.');
        } finally { setSendingEmail(false); }
    };

    // ── CÁLCULOS DE TENDÊNCIA ─────────────────────────────────────────────────
    const trendData = useMemo(() => {
        const sorted = [...allArquivos]
            .filter(a => a.status !== 'sem_movimento')
            .sort((a, b) => a.data_referencia.localeCompare(b.data_referencia))
            .slice(-30);
        const saldos     = sorted.map(a => parseFloat(a.saldo_dia || 0));
        const receitas   = sorted.map(a => parseFloat(a.total_receitas || 0));
        const despesas   = sorted.map(a => parseFloat(a.total_despesas || 0));
        const ultima     = saldos[saldos.length - 1] ?? 0;
        const penultima  = saldos[saldos.length - 2] ?? 0;
        const tendencia  = ultima > penultima ? 'up' : ultima < penultima ? 'down' : 'neutral';
        const mediaReceita = receitas.length ? receitas.reduce((a, b) => a + b, 0) / receitas.length : 0;
        const mediaDespesa = despesas.length ? despesas.reduce((a, b) => a + b, 0) / despesas.length : 0;
        const taxaConvergencia = allArquivos.length
            ? ((allArquivos.filter(a => a.status === 'convergente').length / allArquivos.length) * 100).toFixed(0)
            : 0;
        return { saldos, receitas, despesas, ultima, tendencia, mediaReceita, mediaDespesa, taxaConvergencia, sorted };
    }, [allArquivos]);

    const filteredArquivos = useMemo(() =>
        arquivos.filter(p => !searchTerm || p.data_referencia.includes(searchTerm)),
    [arquivos, searchTerm]);

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="rf-container">

            {/* ── HEADER ── */}
            <header className="rf-header">
                <div className="rf-header-left">
                    <button onClick={() => navigate(-1)} className="rf-btn-back">
                        <FaArrowLeft />
                    </button>
                    <div className="rf-header-icon"><FaArchive /></div>
                    <div className="rf-header-text">
                        <h1>Arquivo Financeiro Digital
                            {currentViewFolder && <span className="rf-breadcrumb"> / {currentViewFolder.nome}</span>}
                        </h1>
                        <p>Central de Auditoria · Dossiês · Inteligência Financeira</p>
                    </div>
                </div>
                <div className="rf-header-actions">
                    {!currentViewFolder && temAcessoFull && (
                        <button className="rf-btn-secondary" onClick={handleCreateFolder}>
                            <FaFolderPlus /> Nova Pasta
                        </button>
                    )}
                    <button className="rf-btn-primary" onClick={handleProcessarManual}
                        style={{ opacity: isAdminGeral ? 1 : 0.5, cursor: isAdminGeral ? 'pointer' : 'not-allowed' }}>
                        <FaCalendarAlt /> Processar Dia
                    </button>
                    <div className="rf-search">
                        <FaSearch className="rf-search-icon" />
                        <input type="date" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
            </header>

            {/* ── INSIGHTS BAR ── */}
            {insights && (
                <section className="rf-insights-bar">
                    <div className="rf-insight-card">
                        <div className="rf-insight-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                            <FaCoins />
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">Saldo Atual</span>
                            <strong className={parseFloat(insights.carteira.saldo || 0) >= 0 ? 'positive' : 'negative'}>
                                {formatCurrencyCompact(insights.carteira.saldo || 0)}
                            </strong>
                        </div>
                        <Sparkline data={trendData.saldos.slice(-10)} color="#10b981" />
                    </div>

                    <div className="rf-insight-card">
                        <div className="rf-insight-icon" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                            <FaArrowDown />
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">A Pagar (vencido)</span>
                            <strong className="negative">
                                {formatCurrencyCompact(insights.pagar.total_vencido || 0)}
                            </strong>
                            <span className="rf-insight-sub">{insights.pagar.qtd_pendentes || 0} pendentes</span>
                        </div>
                    </div>

                    <div className="rf-insight-card">
                        <div className="rf-insight-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                            <FaArrowUp />
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">A Receber</span>
                            <strong className="positive">
                                {formatCurrencyCompact(insights.receber.total_pendente || 0)}
                            </strong>
                            <span className="rf-insight-sub">
                                {formatCurrencyCompact(insights.receber.recebido_mes || 0)} recebido no mês
                            </span>
                        </div>
                    </div>

                    <div className="rf-insight-card">
                        <div className="rf-insight-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                            <FaTachometerAlt />
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">Taxa Convergência</span>
                            <strong>{trendData.taxaConvergencia}%</strong>
                            <span className="rf-insight-sub">últimos {allArquivos.length} fechamentos</span>
                        </div>
                    </div>

                    <div className="rf-insight-card">
                        <div className="rf-insight-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                            <FaChartLine />
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">Média Receita/Dia</span>
                            <strong className="positive">{formatCurrencyCompact(trendData.mediaReceita)}</strong>
                            <span className="rf-insight-sub">
                                Média despesa: {formatCurrencyCompact(trendData.mediaDespesa)}
                            </span>
                        </div>
                        <Sparkline data={trendData.receitas.slice(-10)} color="#f59e0b" />
                    </div>

                    <div className={`rf-insight-card rf-trend-card ${trendData.tendencia}`}>
                        <div className="rf-insight-icon rf-trend-icon">
                            {trendData.tendencia === 'up'   && <FaArrowUp />}
                            {trendData.tendencia === 'down' && <FaArrowDown />}
                            {trendData.tendencia === 'neutral' && <FaMinus />}
                        </div>
                        <div className="rf-insight-body">
                            <span className="rf-insight-label">Tendência</span>
                            <strong>
                                {trendData.tendencia === 'up'   ? 'Ascendente' :
                                 trendData.tendencia === 'down' ? 'Descendente' : 'Estável'}
                            </strong>
                            <span className="rf-insight-sub">último fechamento: {formatCurrencyCompact(trendData.ultima)}</span>
                        </div>
                    </div>
                </section>
            )}

            {/* ── BREADCRUMB / VOLTAR ── */}
            {currentViewFolder && (
                <div className="rf-breadcrumb-bar" onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnFolder(e, null)}>
                    <button onClick={() => setCurrentViewFolder(null)} className="rf-btn-goback">
                        <FaLevelUpAlt /> Voltar para Raiz — arraste arquivos aqui para mover
                    </button>
                </div>
            )}

            {/* ── HEATMAP CALENDAR (SURPRISE!) ── */}
            {!currentViewFolder && allArquivos.length > 0 && (
                <section className="rf-heatmap-section">
                    <div className="rf-section-header">
                        <FaRegCalendarAlt className="rf-section-icon" />
                        <div>
                            <h2>Calendário de Saúde Financeira</h2>
                            <p>Visão panorâmica de 91 dias · Clique numa célula para abrir o dossiê</p>
                        </div>
                        <div className="rf-heatmap-streak">
                            <FaFire />
                            <span>{allArquivos.filter(a => a.status === 'convergente').length} dias convergentes</span>
                        </div>
                    </div>
                    <HeatmapCalendar
                        arquivos={allArquivos}
                        onCellClick={(a) => handleOpenDossie(a.id)}
                    />
                </section>
            )}

            {/* ── ÁREA PRINCIPAL ── */}
            <main className="rf-main">
                {loading ? (
                    <div className="rf-loading"><div className="rf-spinner" /> Organizando arquivos…</div>
                ) : (
                    <>
                        {/* Pastas */}
                        {!currentViewFolder && pastasCustom.length > 0 && (
                            <>
                                <div className="rf-section-divider">Pastas</div>
                                <div className="rf-folders-grid">
                                    {pastasCustom.map(pasta => (
                                        <div key={pasta.id} className="rf-folder-card"
                                            onClick={() => setCurrentViewFolder(pasta)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDropOnFolder(e, pasta.id)}>
                                            <div className="rf-folder-icon">📁</div>
                                            <span>{pasta.nome}</span>
                                            {temAcessoFull && (
                                                <button className="rf-folder-delete"
                                                    onClick={(e) => handleDeleteCustomFolder(e, pasta.id)}>
                                                    <FaTimes />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Arquivos */}
                        {filteredArquivos.length > 0 && (
                            <>
                                {(pastasCustom.length > 0 || currentViewFolder) && (
                                    <div className="rf-section-divider" style={{ marginTop: 24 }}>Fechamentos</div>
                                )}
                                <div className="rf-files-grid">
                                    {filteredArquivos.map(arquivo => {
                                        const score = calcHealthScore(arquivo);
                                        const { label: slabel, cls: scls } = scoreLabel(score);
                                        const saldo = parseFloat(arquivo.saldo_dia || 0);
                                        return (
                                            <div key={arquivo.id}
                                                className={`rf-file-card ${arquivo.status}`}
                                                onClick={() => handleOpenDossie(arquivo.id)}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, arquivo.id)}>

                                                {/* Delete (admin) */}
                                                {isAdminGeral && (
                                                    <button className="rf-file-delete"
                                                        onClick={(e) => handleDeleteDossie(e, arquivo.id)}>
                                                        <FaTrash />
                                                    </button>
                                                )}

                                                {/* Tab da pasta */}
                                                <div className="rf-file-tab" />

                                                {/* Corpo */}
                                                <div className="rf-file-body">
                                                    {/* Score badge */}
                                                    <div className={`rf-score-badge ${scls}`}>
                                                        <span className="rf-score-num">{score}</span>
                                                        <span className="rf-score-label">{slabel}</span>
                                                    </div>

                                                    {/* Ícone principal */}
                                                    <div className="rf-file-icon-wrap">
                                                        <FaFileInvoiceDollar className="rf-file-icon" />
                                                    </div>

                                                    {/* Data */}
                                                    <h3 className="rf-file-date">{formatDateShort(arquivo.data_referencia)}</h3>

                                                    {/* Status pill */}
                                                    <div className="rf-status-pill">
                                                        {arquivo.status === 'convergente'  && <><FaCheckCircle /> OK</>}
                                                        {arquivo.status === 'divergente'   && <><FaExclamationTriangle /> Divergente</>}
                                                        {arquivo.status === 'sem_movimento'&& <>Sem movimento</>}
                                                    </div>

                                                    {/* Saldo */}
                                                    <div className="rf-file-saldo">
                                                        <small>Saldo</small>
                                                        <strong className={saldo >= 0 ? 'positive' : 'negative'}>
                                                            {formatCurrencyCompact(saldo)}
                                                        </strong>
                                                    </div>

                                                    {/* AI badge */}
                                                    {arquivo.tem_analise_ia && (
                                                        <div className="rf-ai-badge" title="Análise IA disponível">
                                                            <FaRobot /> IA
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Empty */}
                        {filteredArquivos.length === 0 && pastasCustom.length === 0 && (
                            <div className="rf-empty">
                                <FaInbox />
                                <p>Nenhum item encontrado.</p>
                                {isAdminGeral && (
                                    <button className="rf-btn-primary" onClick={handleProcessarManual}>
                                        <FaBolt /> Gerar Primeiro Fechamento
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* ── MODAL DOSSIÊ ── */}
            {selectedDossieId && dossieDetails && (
                <div className="rf-overlay" ref={overlayRef} onClick={handleOverlayClick}>
                    <div className="rf-dossie animate-pop-in">
                        <button className="rf-dossie-close" onClick={() => setSelectedDossieId(null)}>
                            <FaTimes />
                        </button>

                        {/* Header do dossiê */}
                        <div className={`rf-dossie-header ${dossieDetails.status}`}>
                            <div className="rf-dossie-header-left">
                                <div className="rf-dossie-seal"><FaFileInvoiceDollar /></div>
                                <div>
                                    <h2>Dossiê Financeiro</h2>
                                    <p>{formatDate(dossieDetails.data_referencia)}</p>
                                </div>
                            </div>
                            <div className="rf-dossie-header-right">
                                <div className={`rf-dossie-stamp ${dossieDetails.status}`}>
                                    {dossieDetails.status === 'convergente'   ? '✓ AUDITADO: OK'        :
                                     dossieDetails.status === 'divergente'    ? '⚠ DIVERGENTE'          :
                                     'SEM MOVIMENTO'}
                                </div>
                                {/* Score no modal */}
                                <div className={`rf-dossie-score ${scoreLabel(calcHealthScore(dossieDetails)).cls}`}>
                                    <span>{calcHealthScore(dossieDetails)}</span>
                                    <small>score</small>
                                </div>
                            </div>
                        </div>

                        <div className="rf-dossie-body">
                            {/* Botões de ação */}
                            <div className="rf-dossie-actions">
                                {!dossieDetails.detalhes_json?.ai_analysis ? (
                                    <button
                                        className={`rf-btn-ai ${dossieDetails.status === 'divergente' ? 'audit' : 'insight'}`}
                                        onClick={handleAnalyzeAI} disabled={analyzing}>
                                        {analyzing
                                            ? <><span className="rf-spinner-sm" /> Processando…</>
                                            : dossieDetails.status === 'divergente'
                                            ? <><FaRobot /> Solicitar Auditoria IA</>
                                            : <><FaLightbulb /> Insights do CFO Virtual</>}
                                    </button>
                                ) : (
                                    <div className={`rf-ai-result animate-pop-in ${dossieDetails.status}`}>
                                        <div className="rf-ai-result-header">
                                            <div>
                                                {dossieDetails.status === 'divergente'
                                                    ? <><FaRobot /> <h3>Análise do Auditor Virtual</h3></>
                                                    : <><FaLightbulb /> <h3>Insights do CFO Virtual</h3></>}
                                            </div>
                                            <span className="rf-saved-badge"><FaCheckDouble /> Salvo</span>
                                        </div>
                                        <div className="rf-ai-content"
                                            dangerouslySetInnerHTML={{ __html: dossieDetails.detalhes_json.ai_analysis }} />
                                    </div>
                                )}

                                {/* Botão enviar e-mail */}
                                <button className="rf-btn-email" onClick={handleSendEmail} disabled={sendingEmail}>
                                    {sendingEmail
                                        ? <><span className="rf-spinner-sm" /> Enviando…</>
                                        : <><FaEnvelope /> Enviar por E-mail</>}
                                </button>
                            </div>

                            {/* Alerta divergência */}
                            {dossieDetails.status === 'divergente' && (
                                <div className="rf-divergence-alert">
                                    <FaExclamationTriangle />
                                    <div>
                                        <h4>Inconsistência Detectada</h4>
                                        <p>{dossieDetails.obs_sistema}</p>
                                    </div>
                                </div>
                            )}

                            {/* Resumo */}
                            <section className="rf-doc-section">
                                <h3><span className="rf-section-num">I.</span> Resumo do Caixa</h3>
                                <div className="rf-summary-grid">
                                    <div className="rf-summary-item income">
                                        <span>(+) Receitas</span>
                                        <strong>{formatCurrency(dossieDetails.total_receitas)}</strong>
                                    </div>
                                    <div className="rf-summary-item expense">
                                        <span>(−) Despesas</span>
                                        <strong>{formatCurrency(dossieDetails.total_despesas)}</strong>
                                    </div>
                                    <div className={`rf-summary-item balance ${parseFloat(dossieDetails.saldo_dia) >= 0 ? 'pos' : 'neg'}`}>
                                        <span>(=) Saldo Líquido</span>
                                        <strong>{formatCurrency(dossieDetails.saldo_dia)}</strong>
                                    </div>
                                </div>
                            </section>

                            {/* Auditoria + Extrato */}
                            <div className="rf-two-col">
                                <section className="rf-doc-section">
                                    <h3><span className="rf-section-num">II.</span> Auditoria Cruzada</h3>
                                    <ul className="rf-audit-list">
                                        <li>
                                            <span>Baixado em CP:</span>
                                            <strong>{formatCurrency(dossieDetails.detalhes_json?.auditoria?.total_pagar_modulo)}</strong>
                                        </li>
                                        <li>
                                            <span>Baixado em CR:</span>
                                            <strong>{formatCurrency(dossieDetails.detalhes_json?.auditoria?.total_receber_modulo)}</strong>
                                        </li>
                                        <li className="rf-audit-info">
                                            <small>{dossieDetails.qtd_transacoes} transações processadas</small>
                                        </li>
                                    </ul>
                                </section>
                                <section className="rf-doc-section">
                                    <h3><span className="rf-section-num">III.</span> Extrato Analítico</h3>
                                    <div className="rf-mini-statement">
                                        {(dossieDetails.detalhes_json?.movimentacoes || []).slice(0, 8).map((mov, idx) => (
                                            <div key={idx} className="rf-statement-row">
                                                <span className="rf-row-desc">{String(mov.descricao || '').substring(0, 32)}…</span>
                                                <span className={`rf-row-val ${parseFloat(mov.valor) >= 0 ? 'pos' : 'neg'}`}>
                                                    {formatCurrency(mov.valor)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            {/* RH */}
                            {(dossieDetails.detalhes_json?.rh?.total_salarios > 0 || dossieDetails.detalhes_json?.rh?.total_beneficios > 0) && (
                                <section className="rf-doc-section rf-rh-section">
                                    <h3><span className="rf-section-num">IV.</span> Custos de Pessoal</h3>
                                    <div className="rf-rh-grid">
                                        <div><span>Folha:</span><strong>{formatCurrency(dossieDetails.detalhes_json.rh.total_salarios)}</strong></div>
                                        <div><span>Encargos:</span><strong>{formatCurrency(dossieDetails.detalhes_json.rh.total_beneficios)}</strong></div>
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="rf-dossie-footer">
                            <div className="rf-footer-line" />
                            <p>Documento auditado eletronicamente — ID #{String(dossieDetails.id).padStart(6, '0')}</p>
                            <p>Processado em: {new Date(dossieDetails.data_processamento).toLocaleString('pt-BR')}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}